// Floating Ember chat-widget. PRD-02 §6.1 + §3.8.
// Mobile: fullscreen overlay. Desktop: 400x600 panel rechtsonder.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { MessageBubble, type ChatMessage } from "./MessageBubble";

interface ChatResponse {
  conversationId: string;
  reply: string;
  tokensUsed: number;
}

interface MessagesResponse {
  conversationId: string;
  messages: Array<{
    id: string;
    role: "user" | "assistant" | "system";
    content: string;
    createdAt: string;
  }>;
}

interface ConversationsResponse {
  conversations: Array<{ id: string; updatedAt: string }>;
}

export function EmberChat() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Only show widget for client-rol
  const enabled = user?.role === "client";

  // Discover latest conversation on first open
  const convsQuery = useQuery({
    queryKey: ["ember-conversations"],
    queryFn: () => api.get<ConversationsResponse>("/v1/ember/conversations"),
    enabled: enabled && open,
  });

  useEffect(() => {
    if (!conversationId && convsQuery.data?.conversations[0]) {
      setConversationId(convsQuery.data.conversations[0].id);
    }
  }, [convsQuery.data, conversationId]);

  // Load messages of active conversation
  const messagesQuery = useQuery({
    queryKey: ["ember-messages", conversationId],
    queryFn: () => api.get<MessagesResponse>(`/v1/ember/conversations/${conversationId}/messages`),
    enabled: enabled && open && !!conversationId,
  });

  const send = useMutation({
    mutationFn: (message: string) =>
      api.post<ChatResponse>("/v1/ember/chat", {
        message,
        conversationId: conversationId ?? undefined,
      }),
    onMutate: (message) => {
      setPendingMessage(message);
      setDraft("");
      setErrorKey(null);
    },
    onSuccess: (data) => {
      setConversationId(data.conversationId);
      setPendingMessage(null);
      qc.invalidateQueries({ queryKey: ["ember-messages", data.conversationId] });
      qc.invalidateQueries({ queryKey: ["ember-conversations"] });
    },
    onError: (e) => {
      setPendingMessage(null);
      setErrorKey(e instanceof ApiError ? e.i18nKey : "errors.internal");
    },
  });

  // Auto-scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messagesQuery.data?.messages.length, pendingMessage, send.isPending]);

  if (!enabled) return null;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!draft.trim() || send.isPending) return;
    send.mutate(draft.trim());
  }

  const messages: ChatMessage[] = messagesQuery.data?.messages.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    createdAt: new Date(m.createdAt),
  })) ?? [];

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={t("ember.open", "Open Ember chat")}
          className="fixed bottom-4 right-4 w-14 h-14 rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-lg flex items-center justify-center text-2xl z-50 transition-transform hover:scale-105"
        >
          💬
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-4 sm:right-4 sm:w-[400px] sm:h-[600px] bg-white shadow-2xl sm:rounded-2xl border border-neutral-200 flex flex-col z-50">
          <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500" aria-hidden />
              <h2 className="font-semibold text-sm">{t("ember.title", "Ember")}</h2>
              <span className="text-xs text-neutral-500">
                {t("ember.subtitle", "AI-assistent")}
              </span>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label={t("ember.close", "Sluit")}
              className="text-neutral-500 hover:text-neutral-900 text-xl leading-none"
            >
              ×
            </button>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-neutral-50">
            {messages.length === 0 && !send.isPending && (
              <div className="text-center text-sm text-neutral-500 mt-8">
                <p className="font-medium mb-1">{t("ember.greeting", "Hoi, ik ben Ember.")}</p>
                <p>{t("ember.intro", "Stel een vraag over je dossier of het BBZ-traject.")}</p>
                <p className="mt-3 text-xs text-amber-700">
                  {t(
                    "ember.disclaimer",
                    "⚠ Ik geef geen financieel of juridisch advies. Daarvoor verwijs ik je door naar je counselor."
                  )}
                </p>
              </div>
            )}
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
            {pendingMessage && (
              <MessageBubble
                message={{
                  id: "pending",
                  role: "user",
                  content: pendingMessage,
                  createdAt: new Date(),
                }}
              />
            )}
            {send.isPending && (
              <MessageBubble
                message={{
                  id: "typing",
                  role: "assistant",
                  content: "···",
                  createdAt: new Date(),
                }}
                typing
              />
            )}
            {errorKey && (
              <div role="alert" className="mx-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                {t(errorKey)}
              </div>
            )}
          </div>

          <form onSubmit={onSubmit} className="border-t border-neutral-200 p-3">
            <div className="flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    onSubmit(e);
                  }
                }}
                placeholder={t("ember.placeholder", "Stel een vraag…")}
                rows={1}
                className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-sm resize-none max-h-32 focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                disabled={send.isPending}
              />
              <button
                type="submit"
                disabled={!draft.trim() || send.isPending}
                className="px-3 py-2 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
                aria-label={t("ember.send", "Versturen")}
              >
                ↑
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
